import axios, { type AxiosResponse } from 'axios'

import { type SanityFrameBuilderProductExtraService } from '../data/sanity/queries/types/extra-services'
import { type SanityFrameBuilderProductGlass } from '../data/sanity/queries/types/glass'
import {
  type SanityFrameBuilderProductPassepartoutColor,
  type SanityFrameBuilderProductPassepartoutSize,
} from '../data/sanity/queries/types/passepartout'
import { type SanityFrameBuilderProductProfile } from '../data/sanity/queries/types/profile'
import {
  type SanityFrameBuilderProductSpacerColor,
  type SanityFrameBuilderProductSpacerSize,
} from '../data/sanity/queries/types/spacer'
import { type SanityFrameBuilderProductTexture } from '../data/sanity/queries/types/texture'
import { type SanityFrameBuilderProductConfiguration } from '../data/sanity/queries/types'
import { type FrameBuilderState } from './state'
import {
  getIsProductPassepartoutSizeValid,
  getIsProductProfileAvailableForArtSize,
  getIsProductProfileAvailableForPassepartoutColor,
  getIsProductProfileAvailableForTexture,
} from './validation/product-is-valid'

export class ProductValidationError extends Error {}

export type FrameBuilderProductConfiguration =
  SanityFrameBuilderProductConfiguration

export type FrameBuilderProductProfile = SanityFrameBuilderProductProfile

export type FrameBuilderProductTexture = SanityFrameBuilderProductTexture

export type FrameBuilderProductPassepartoutSize =
  SanityFrameBuilderProductPassepartoutSize

export type FrameBuilderProductPassepartoutColor =
  SanityFrameBuilderProductPassepartoutColor

export type FrameBuilderProductGlass = SanityFrameBuilderProductGlass

export type FrameBuilderProductSpacerSize = SanityFrameBuilderProductSpacerSize

export type FrameBuilderProductSpacerColor =
  SanityFrameBuilderProductSpacerColor

export type FrameBuilderProductExtraService =
  SanityFrameBuilderProductExtraService

interface FrameBuilderProductArtwork {
  width: number
  height: number
}

interface FrameBuilderProductPassepartout {
  size: FrameBuilderProductPassepartoutSize
  color?: FrameBuilderProductPassepartoutColor
}

interface FrameBuilderProductSpacer {
  size?: FrameBuilderProductSpacerSize
  color?: FrameBuilderProductSpacerColor
}

interface FrameBuilderProductExtras {
  active: boolean
  extraServices: FrameBuilderProductExtraService[]
  spacer: FrameBuilderProductSpacer
}

export interface FrameBuilderProduct {
  artwork: FrameBuilderProductArtwork
  texture: FrameBuilderProductTexture
  profile: FrameBuilderProductProfile
  passepartout: FrameBuilderProductPassepartout
  glass: FrameBuilderProductGlass
  extras: FrameBuilderProductExtras
}

interface FrameBuilderArtworkPayload {
  width: number
  height: number
}

interface FrameBuilderPassepartoutPayload {
  sizeId?: string
  colorId?: string
}

interface FrameBuilderExtrasPayload {
  active: boolean
  extraServiceIds: string[]
  spacer: {
    sizeId?: string
    colorId?: string
  }
}

export interface FrameBuilderPayload {
  artwork: FrameBuilderArtworkPayload
  textureId?: string
  profileId?: string
  passepartout: FrameBuilderPassepartoutPayload
  glassId?: string
  extras: FrameBuilderExtrasPayload
  image: string
}

export interface ShopifyCreateFrameProductResponse {
  productId: string
  variantId: string
}

/**
 * Gets product data from frame builder payload.
 */
export const getProductFromPayload = (
  frameBuilderPayload: FrameBuilderPayload,
  configuration: FrameBuilderProductConfiguration,
) => {
  // Resolve actual profiles and textures
  const profile = configuration.profiles.find(
    (profile) => profile._id === frameBuilderPayload.profileId,
  )
  const texture = configuration.textures.find(
    (texture) => texture._id === frameBuilderPayload.textureId,
  )

  // Resolve actual passepartout sizes & colors
  const passepartoutSize = configuration.passepartoutSizes.find(
    (passepartoutSize) =>
      passepartoutSize._id === frameBuilderPayload.passepartout.sizeId,
  )
  const passepartoutColor = configuration.passepartoutColors.find(
    (passepartoutColor) =>
      passepartoutColor._id === frameBuilderPayload.passepartout.colorId,
  )

  // Resolve actual glass
  const glass = configuration.glass.find(
    (glass) => glass._id === frameBuilderPayload.glassId,
  )

  // Resolve actual spacer sizes & colors
  const spacerSize = configuration.spacerSizes.find(
    (spacerSize) => spacerSize._id === frameBuilderPayload.extras.spacer.sizeId,
  )
  const spacerColor = configuration.spacerColors.find(
    (spacerColor) =>
      spacerColor._id === frameBuilderPayload.extras.spacer.colorId,
  )

  const frameWidth =
    frameBuilderPayload.artwork.width + (passepartoutSize?.size ?? 0) * 2
  const frameHeight =
    frameBuilderPayload.artwork.height + (passepartoutSize?.size ?? 0) * 2

  if (typeof texture === 'undefined') {
    throw new ProductValidationError('Frame texture is missing.')
  }

  if (typeof profile === 'undefined') {
    throw new ProductValidationError('Frame profile is missing.')
  }

  if (typeof passepartoutSize === 'undefined') {
    throw new ProductValidationError('Frame passepartout size is missing.')
  }

  if (typeof glass === 'undefined') {
    throw new ProductValidationError('Frame glass is missing.')
  }

  if (
    !getIsProductProfileAvailableForArtSize([frameWidth, frameHeight], profile)
  ) {
    throw new ProductValidationError('Frame profile is invalid for frame size.')
  }

  if (!getIsProductProfileAvailableForTexture(profile, texture)) {
    throw new ProductValidationError(
      'Frame profile and texture are incompatible.',
    )
  }

  if (
    !getIsProductPassepartoutSizeValid(
      frameWidth,
      frameHeight,
      passepartoutSize,
      passepartoutColor,
    )
  ) {
    throw new ProductValidationError('Frame passepartout size is invalid.')
  }

  if (
    passepartoutSize.size > 0 &&
    (typeof passepartoutColor === 'undefined' ||
      !getIsProductProfileAvailableForPassepartoutColor(
        profile,
        passepartoutColor,
      ))
  ) {
    throw new ProductValidationError('Frame passepartout color is invalid.')
  }

  const product: FrameBuilderProduct = {
    artwork: {
      width: frameBuilderPayload.artwork.width,
      height: frameBuilderPayload.artwork.height,
    },
    profile,
    texture,
    passepartout: {
      size: passepartoutSize,
      color: passepartoutColor,
    },
    glass: glass,
    extras: {
      active: frameBuilderPayload.extras.active,
      extraServices: [
        ...configuration.extraServices.filter((extraService) =>
          frameBuilderPayload.extras.extraServiceIds.some(
            (extraServiceId) => extraServiceId === extraService._id,
          ),
        ),
      ],
      spacer: {
        size: spacerSize,
        color: spacerColor,
      },
    },
  }

  return product
}

/**
 * Sends a request to create a frame product.
 */
export const createFrameProduct = async (
  locale: string,
  state: FrameBuilderState,
  imageDataUri: string,
) => {
  try {
    const productPayload: FrameBuilderPayload = {
      artwork: {
        width: state.artwork.width,
        height: state.artwork.height,
      },
      textureId: state.texture?._id,
      profileId: state.profile?._id,
      passepartout: {
        sizeId: state.passepartout.size?._id,
        colorId: state.passepartout.color?._id,
      },
      glassId: state.glass?._id,
      extras: {
        active: !!state.extras.active,
        extraServiceIds:
          state.extras.extraServices?.map((extraService) => extraService._id) ??
          [],
        spacer: {
          sizeId: state.extras.spacer.size?._id,
          colorId: state.extras.spacer.color?._id,
        },
      },
      image: imageDataUri,
    }
    const payload = JSON.stringify(productPayload)

    const response = await axios.post<
      ShopifyCreateFrameProductResponse,
      AxiosResponse<ShopifyCreateFrameProductResponse>,
      string
    >('/api/frame-builder/create-frame-product', payload, {
      headers: {
        'Content-Type': 'application/json',
        'X-Locale': locale,
      },
    })

    if (response.status !== 200) {
      throw new Error(JSON.stringify(response.data))
    }

    return response.data
  } catch (error) {
    console.log(error)
  }
}
